// "Print questions by ID" (BRIEFS C11.3) — /admin/v2/print?ids=12,45,…
// Route OUTSIDE AdminLayout (like the test review) so the browser prints only the questions.
// The owner pastes question IDs (any separators; "Q 12345" is fine), presses Show, then
// Print / Save PDF. Same cards as the test review, without a student's answer.
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { v2PrintQuestions } from '../../../api/v2admin';
import { PRINT_CSS, PrintQuestion, fmtDate } from './PrintParts';

export default function AdminV2Print() {
  const [params, setParams] = useSearchParams();
  const ids = params.get('ids') || '';
  const [box, setBox] = useState(ids);
  useEffect(() => { setBox(ids); }, [ids]);

  const q = useQuery({
    queryKey: ['v2PrintQuestions', ids],
    queryFn: () => v2PrintQuestions(ids),
    enabled: ids.trim().length > 0,
    retry: false,
  });

  const show = () => {
    const clean = (box.match(/\d+/g) || []).join(',');
    setParams(clean ? { ids: clean } : {});
  };

  const data = q.data;
  return (
    <div className="pq-page" style={{ maxWidth: 900, margin: '0 auto', padding: '28px 20px', fontFamily: 'DM Sans, sans-serif' }}>
      <style>{PRINT_CSS}</style>
      <div className="no-print" style={{ marginBottom: 24 }}>
        <Link to="/admin" style={{ fontSize: 14, color: '#4F46E5' }}>← Back to admin</Link>
        <h1 style={{ fontSize: 24, fontFamily: 'Sora', margin: '10px 0 6px' }}>Print questions by ID</h1>
        <p style={{ fontSize: 14, color: '#475569', marginBottom: 10 }}>
          Paste question IDs (for example <code>12, 45, 78</code> or one per line). Then press Show, and Print / Save PDF.
        </p>
        <textarea data-testid="pq-ids" value={box} onChange={(e) => setBox(e.target.value)} rows={3}
          placeholder="12, 45, 78"
          style={{ width: '100%', boxSizing: 'border-box', padding: 10, fontSize: 14, borderRadius: 8, border: '1px solid #CBD5E1', fontFamily: 'monospace' }} />
        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <button className="btn btn-primary" data-testid="pq-show" onClick={show}>Show</button>
          {data && data.questions.length > 0 && (
            <button className="btn btn-ghost" onClick={() => window.print()}>Print / Save PDF</button>
          )}
        </div>
        {q.isError && (
          <p data-testid="pq-error" style={{ color: '#B91C1C', marginTop: 10 }}>
            {q.error?.response?.data?.detail || 'Could not load the questions. Press Show again.'}
          </p>
        )}
        {data && data.missing.length > 0 && (
          <p data-testid="pq-missing" style={{ color: '#B45309', marginTop: 10 }}>
            Not found: {data.missing.join(', ')}
          </p>
        )}
      </div>

      {q.isLoading && ids && <div className="spinner" />}
      {data && data.questions.length > 0 && (
        <>
          <div style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 20, fontFamily: 'Sora', marginBottom: 2 }}>Questions</h2>
            <p style={{ fontSize: 13, color: '#64748B' }}>
              {data.questions.length} question{data.questions.length === 1 ? '' : 's'} · {fmtDate(new Date().toISOString())}
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {data.questions.map((x, i) => <PrintQuestion key={x.question_id} q={x} index={i + 1} />)}
          </div>
        </>
      )}
      {data && data.questions.length === 0 && (
        <p style={{ color: '#64748B' }}>No questions found for these IDs.</p>
      )}
    </div>
  );
}
